import { useState } from 'react'
import { Link } from 'react-router'
import type { Alarm, Station } from '../../api/types'
import { VENDOR_LABEL } from '../../api/types'
import { lastObservation, statusTone, statusWord, type SystemStatus } from '../../lib/freshness'
import { duration } from '../../lib/health'
import {
  FRESHNESS_LEGEND, FRESHNESS_WORD, freshTitle, SOURCE_LABEL, type StationSource, type StationView, stationCount, stationsEmptyMessage,
} from '../../lib/overviewStatus'
import { sourceDetail } from '../../lib/stationSource'
import { formatDateTime, relativeAge } from '../../lib/time'
import { AlarmCell } from '../Alarms'

type NameOrder = 'asc' | 'desc'

/** Stations needing attention always on top (Tamar, 2026-11-01), then each
 *  group by name, A to Z or Z to A. */
export function sortStations(stations: Station[], order: NameOrder, flagged: ReadonlySet<string>): Station[] {
  const byName = (a: Station, b: Station) => (order === 'asc' ? 1 : -1) * a.name.localeCompare(b.name)
  return [...stations].sort((a, b) => Number(flagged.has(b.code)) - Number(flagged.has(a.code)) || byName(a, b))
}

const SOURCES: { key: StationSource | null; label: string }[] = [
  { key: null, label: 'All' }, { key: 'mrl', label: SOURCE_LABEL.mrl }, { key: 'ws', label: SOURCE_LABEL.ws },
]

/** The station list the Stations page was: every station in the project
 *  filter, narrowed by the page's search, its source and the attention
 *  switch, one row per station linking to its readings. Shown open, since
 *  it is now the only list of stations. */
export function StationList({ view, total, filtered, query, attention, source, onSource, onAttention, onClearSearch,
                              statusOf, staleOf, alarms, now, loading, failed }: {
  view: StationView
  /** Every station the reader can see: the M of "N of M". */
  total: number
  /** Anything narrows the list (search, attention, source or project). */
  filtered: boolean
  query: string; attention: boolean; source: StationSource | null
  onSource: (source: StationSource | null) => void
  onAttention: (attention: boolean) => void
  onClearSearch: () => void
  statusOf: (station: Station) => SystemStatus
  staleOf: (station: Station) => boolean
  alarms: Map<string, Alarm[]>
  now: Date
  loading: boolean
  /** The first load failed: there is no list to show. */
  failed: boolean
}) {
  const [order, setOrder] = useState<NameOrder>('asc')
  const shown = sortStations(view.shown, order, new Set(view.flagged.map(station => station.code)))
  return <section className="overview-panel ov-stations" aria-labelledby="overview-stations-title">
    <div className="ov-stations-head">
      <h2 id="overview-stations-title">Stations <span className="overview-inline-count">{loading ? '…' : view.inProject.length}</span></h2>
      <div className="ov-stations-tools">
        <div className="segmented" role="group" aria-label="Filter stations by source">
          {SOURCES.map(({ key, label }) => <button key={label} type="button" aria-pressed={source === key} onClick={() => onSource(key)}>
            {label} <span className="count">({view.sourceCounts[key ?? 'all']})</span>
          </button>)}
        </div>
        {/* A sort, not a filter (Tamar, 2026-10-28): the stations the Needs
            Attention queue lists are always on top, and one button turns
            the name order (2026-11-01). An old ?attention=1 link still
            narrows, and says how to undo it. */}
        {attention
          ? <button type="button" className="button" aria-pressed onClick={() => onAttention(false)}>Show all stations</button>
          : <button type="button" className="ov-sort" onClick={() => setOrder(was => was === 'asc' ? 'desc' : 'asc')}
              aria-label={`Sorted by name, ${order === 'asc' ? 'A to Z' : 'Z to A'}; stations needing attention first. Reverse the order`}
              title={`Name ${order === 'asc' ? 'A–Z' : 'Z–A'}; stations needing attention stay on top`}>
            {/* The usual sort icon, two parallel arrows; the one for the
                current direction solid, the other faded. */}
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 20V4M4 8l4-4 4 4" opacity={order === 'asc' ? 1 : 0.35} />
              <path d="M16 4v16M12 16l4 4 4-4" opacity={order === 'desc' ? 1 : 0.35} />
            </svg>
          </button>}
      </div>
    </div>
    {loading ? <p className="ov-stations-empty" role="status">Loading stations…</p>
      : failed ? <p className="ov-stations-empty" role="status">The station list could not load. Refresh to try again.</p>
        : !total ? <p className="ov-stations-empty" role="status">No stations registered.</p>
          : shown.length ? <StationTable stations={shown} statusOf={statusOf} staleOf={staleOf} alarms={alarms} now={now}
            count={stationCount(shown.length, total, filtered)} />
            : <div className="ov-stations-empty">
              <p role="status">{stationsEmptyMessage(query, attention, source)}</p>
              {query && <button type="button" className="button" onClick={onClearSearch}>Clear search</button>}
            </div>}
  </section>
}

/** The stations as one table, each row one link to the station (the board's
 *  grid rows: a table row cannot be a link). Under 900 px it scrolls inside
 *  its own box rather than squeezing the columns. */
function StationTable({ stations, statusOf, staleOf, alarms, now, count }: {
  stations: Station[]; statusOf: (station: Station) => SystemStatus; staleOf: (station: Station) => boolean
  alarms: Map<string, Alarm[]>; now: Date; count: string
}) {
  return <div className="st-table">
    <div className="st-scroll">
      <div className="st-grid">
        <div className="st-head">
          <span>Station</span><span>Source</span><span>System Status</span><span>Active Alarms</span>
          <span className="st-obs">Last Observation <span className="ov-tz">UTC+4</span></span>
        </div>
        {stations.map(station => {
          const status = statusOf(station)
          const { expected_interval_s: expected, silent_after_s: silentAfter } = station
          // While the monitor calls the station silent, its own last real
          // reading; otherwise the newest. No colour without the monitor's
          // numbers, nor while its checks cannot vouch for the station:
          // never one judged by another rule.
          const { at: last, fresh } = lastObservation(station, now, staleOf(station))
          const age = last ? relativeAge(last, now) : 'never read'
          return <Link key={station.code} className="st-row" to={`/explore/${encodeURIComponent(station.code)}`}>
            <span className="st-cell">
              <span className="st-name">{station.name}</span>
              <span className="st-sub mono">{station.code}</span>
              {!!station.projects?.length && <span className="st-sub">{station.projects.join(', ')}</span>}
            </span>
            <span className="st-cell">
              <span>{VENDOR_LABEL[station.vendor]}</span>
              <span className="st-sub">{sourceDetail(station)}</span>
            </span>
            <span className="st-cell">
              <span className={`ss ss-${statusTone(status)}`}>{statusWord(status)}</span>
              {status.note && <span className="st-sub">{status.note}</span>}
            </span>
            <span className="st-cell"><AlarmCell alarms={alarms.get(station.code) ?? []} /></span>
            <span className={`st-cell st-obs${fresh ? ` fresh-${fresh}` : ''}`}
              title={fresh && expected && silentAfter ? freshTitle(fresh, duration(expected * 1000), duration(silentAfter * 1000)) : undefined}>
              <span className="mono st-time">{last ? formatDateTime(last) : 'Never'}</span>
              {/* The colour's word beside it: a status colour never stands alone. */}
              <span className="st-age">{fresh && last ? `${FRESHNESS_WORD[fresh]}, ${age}` : age}</span>
            </span>
          </Link>
        })}
      </div>
    </div>
    <div className="st-foot">
      <span role="status">{count}</span>
      <span className="fresh-legend">
        Last Observation:
        {FRESHNESS_LEGEND.map(item => <span key={item.fresh} className="legend-item"><span className={`swatch fresh-${item.fresh}`} />{item.text}</span>)}
      </span>
    </div>
  </div>
}
